import React, { useState } from 'react';
import { Card, Button, message, Descriptions, List, Tag, Space, Typography } from 'antd';
import { SyncOutlined, BugOutlined } from '@ant-design/icons';
import { get } from '../utils/request';

const { Title, Text, Paragraph } = Typography;

const WechatGroupDebug: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [debugData, setDebugData] = useState<any>(null);

  const fetchDebugData = async () => {
    setLoading(true);
    try {
      const data = await get('/wechat/groups/debug/raw-list', {
        params: { limit: 20 }
      });
      setDebugData(data);
      message.success('获取调试数据成功');
    } catch (error) {
      console.error('Failed to fetch debug data:', error);
      message.error('获取调试数据失败');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div>
            <Title level={3}>
              <BugOutlined /> 企业微信群聊调试工具
            </Title>
            <Paragraph type="secondary">
              此工具用于调试企业微信客户群同步功能，查看API返回的原始数据
            </Paragraph>
          </div>

          <Button
            type="primary"
            icon={<SyncOutlined spin={loading} />}
            onClick={fetchDebugData}
            loading={loading}
            size="large"
          >
            获取企业微信群聊原始数据
          </Button>

          {debugData && (
            <>
              <Card title="API 响应概览" type="inner">
                <Descriptions column={2}>
                  <Descriptions.Item label="状态">
                    <Tag color={debugData.success ? 'success' : 'error'}>
                      {debugData.success ? '成功' : '失败'}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="群聊总数">
                    {debugData.total_count}
                  </Descriptions.Item>
                  <Descriptions.Item label="是否有下一页">
                    {debugData.next_cursor ? '是' : '否'}
                  </Descriptions.Item>
                  {debugData.next_cursor && (
                    <Descriptions.Item label="下一页游标">
                      <Text code>{debugData.next_cursor}</Text>
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </Card>

              <Card title="群聊列表（简要信息）" type="inner">
                <List
                  dataSource={debugData.group_list || []}
                  renderItem={(item: any, index: number) => (
                    <List.Item>
                      <Space>
                        <Tag color="blue">#{index + 1}</Tag>
                        <Text strong>Chat ID:</Text>
                        <Text code>{item.chat_id}</Text>
                        <Text type="secondary">Status: {item.status}</Text>
                      </Space>
                    </List.Item>
                  )}
                />
              </Card>

              <Card title="群聊详细信息（前5个样本）" type="inner">
                <List
                  dataSource={debugData.detailed_samples || []}
                  renderItem={(group: any, index: number) => (
                    <List.Item>
                      <Card size="small" style={{ width: '100%' }}>
                        {group.error ? (
                          <div>
                            <Tag color="error">错误</Tag>
                            <Text type="danger">{group.error}</Text>
                            <br />
                            <Text type="secondary">Chat ID: {group.chat_id}</Text>
                          </div>
                        ) : (
                          <Descriptions column={1} size="small">
                            <Descriptions.Item label="群聊名称">
                              <Text strong>{group.name || '未命名'}</Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="Chat ID">
                              <Text code>{group.chat_id}</Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="群主">
                              {group.owner}
                            </Descriptions.Item>
                            <Descriptions.Item label="成员数量">
                              {group.member_list?.length || 0}
                            </Descriptions.Item>
                            <Descriptions.Item label="创建时间">
                              {group.create_time ? new Date(group.create_time * 1000).toLocaleString() : '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label="群公告">
                              {group.notice || '-'}
                            </Descriptions.Item>
                          </Descriptions>
                        )}
                      </Card>
                    </List.Item>
                  )}
                />
              </Card>

              <Card title="原始 JSON 数据" type="inner">
                <pre style={{ 
                  background: '#f5f5f5', 
                  padding: '16px', 
                  borderRadius: '4px',
                  overflow: 'auto',
                  maxHeight: '400px'
                }}>
                  {JSON.stringify(debugData, null, 2)}
                </pre>
              </Card>
            </>
          )}
        </Space>
      </Card>
    </div>
  );
};

export default WechatGroupDebug;
